import React, { useState, memo, useCallback, Fragment } from 'react';
import moment from 'moment';
import Dropdown from '../Dropdown';
import PropTypes from 'prop-types';
import Tab from '../Tab';
import TimePicker from '../TimePicker';
import Button from '../Button';

import { defaultInterval, defaultTimeRange } from './defaultData';
import { useUpdateEffect } from '../../common/hooks';

const { RangePicker } = TimePicker;
const { TabPanel } = Tab;

const timeFormat = time => (
  <Fragment>
    <span className="mgr5">{moment(time).format('YYYY/MM/DD')}</span>
    <span>{moment(time).format('HH:mm:ss')}</span>
  </Fragment>
);

function AutoRefresh({
  onChange,
  interval: refresh,
  timeRange: timeRangeProp,
  timeFrom,
  timeTo,
  intervalOption,
  timeRangeOption,
  className
}) {
  const [timeRange, setTimeRange] = useState(timeRangeProp);
  const [time, setTime] = useState({ timeFrom, timeTo });
  const [interval, setInterval] = useState(refresh);
  const [overlayShow, setOverlayShow] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [tabVal, setTabVal] = useState('auto');

  useUpdateEffect(() => {
    setInterval(refresh);
    setTimeRange(timeRangeProp);
    setTime({
      timeFrom,
      timeTo
    });
    if (!timeRangeProp) {
      setTabVal('exact');
    }
  }, [refresh, timeRangeProp, timeFrom, timeTo]);

  const updateTime = useCallback(val => {
    setTime({
      timeFrom: Math.round(val.start / 1000),
      timeTo: Math.round(val.end / 1000)
    });
    setTimeOpen(false);
  });

  const onOk = useCallback(val => {
    setOverlayShow(false);
    if (tabVal === 'auto' && timeRange) {
      onChange({
        timeRange,
        interval
      });
    } else {
      if (tabVal === 'auto' && !timeRange) {
        setTabVal('exact');
      }
      if (time) {
        onChange({
          timeFrom: time.timeFrom,
          timeTo: time.timeTo,
          timeRange: null
        });
      }
    }
  });

  const renderOverlay = useCallback(() => (
    <div className="auto-refresh-overlay">
      <Tab
        onChange={setTabVal}
        defaultActiveKey={tabVal}
        theme="title"
        className="blue"
      >
        <TabPanel header="自动刷新" keys="auto">
          <div className="option">
            <div className="option-label">时间范围</div>
            <ul className="option-list">
              {timeRangeOption.map((rangeItem, index) => (
                <li
                  key={index}
                  onClick={() => setTimeRange(rangeItem.value)}
                  className={rangeItem.value === timeRange ? 'selected' : ''}
                >
                  {rangeItem.label}
                </li>
              ))}
            </ul>
          </div>
          <div className="option">
            <div className="option-label">刷新频率</div>
            <ul className="option-list">
              {intervalOption.map((item, index) => (
                <li
                  key={index}
                  onClick={() => {
                    if (timeRange) {
                      setInterval(item.value);
                    }
                  }}
                  className={` ${
                    timeRange
                      ? item.value === interval
                        ? 'selected'
                        : 'item'
                      : 'disabled'
                  }`}
                >
                  {item.label}
                </li>
              ))}
            </ul>
          </div>
        </TabPanel>
        <TabPanel header="精确时间" keys="exact">
          <div className="range-wrapper">
            <RangePicker
              open={timeOpen}
              onOpenChange={setTimeOpen}
              defaultValue={{
                start: time.timeFrom * 1000,
                end: time.timeTo * 1000
              }}
              onOk={updateTime}
            />
          </div>
        </TabPanel>
      </Tab>
      <div className="footer">
        <Button
          className="mgr10"
          type="white"
          mini
          onClick={() => setOverlayShow(false)}
        >
          取消
        </Button>
        <Button type="primary" mini onClick={onOk}>
          确定
        </Button>
      </div>
    </div>
  ));

  const renderTimeRange = useCallback(() => {
    if (timeRange) {
      return (
        <span>
          <span className="auto-refresh-value-label">时间范围：</span>
          <span className="auto-refresh-value-value">
            {timeRangeOption.filter(i => i.value === timeRange)[0].label}
          </span>
        </span>
      );
    }
    return (
      <span>
        <span className="auto-refresh-value-label">时间范围：</span>
        <span className="auto-refresh-value-value">
          {timeFormat(time.timeFrom * 1000)}-{timeFormat(time.timeTo * 1000)}
        </span>
      </span>
    );
  });

  return (
    <Dropdown
      overlay={renderOverlay()}
      style={{ width: '450px', textAlign: 'right' }}
      visible={overlayShow}
      onVisibleChange={setOverlayShow}
      shouldCloseWhenClickAway={() => !timeOpen}
      className={className}
    >
      <div className="auto-refresh-value-str">
        {renderTimeRange()}
        <span className="mgl10">
          <span className="auto-refresh-value-label">刷新频率：</span>
          <span className="auto-refresh-value-value">
            {intervalOption.filter(i => i.value === interval)[0].label}
          </span>
        </span>
      </div>
    </Dropdown>
  );
}

AutoRefresh.defaultProps = {
  className: '',
  intervalOption: defaultInterval,
  timeRangeOption: defaultTimeRange
};

AutoRefresh.propTypes = {
  /** 刷新间隔 */
  interval: PropTypes.number,
  /** 刷新间隔选项 */
  intervalOption: PropTypes.array,
  /** 所选时间段 */
  timeRange: PropTypes.string,
  /** 时间段选项 */
  timeRangeOption: PropTypes.array,
  /** 开始时间,与timeRange互斥 */
  timeFrom: PropTypes.number,
  /** 结束时间,与timeRange互斥 */
  timeTo: PropTypes.number,
  /** 自定义class */
  className: PropTypes.string,
  /** 修改回调函数 */
  onChange: PropTypes.func
};

export default memo(AutoRefresh);
